import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation, Trans } from 'react-i18next';
import { MobileSheet } from '../MobileSheet';
import { NmButton } from '../NmButton';

export type GarminGuideMode = 'connect' | 'help';

export interface GarminConnectSheetProps {
  open: boolean;
  mode: GarminGuideMode;
  onClose: () => void;
  /** connect mode — proceed to Garmin OAuth */
  onContinue?: () => void;
  /** connect mode — dismiss without connecting */
  showLater?: boolean;
}

function isElectronShell(): boolean {
  return !!(window as Window & { electronAPI?: unknown }).electronAPI;
}

function GuideSteps({ mode }: { mode: GarminGuideMode }) {
  const { t } = useTranslation();
  const prefix =
    mode === 'connect'
      ? 'athleteSettings.connections.garminConnectSheet'
      : 'athleteSettings.connections.garminHelp';

  return (
    <div className="garmin-guide__body">
      {mode === 'connect' ? (
        <>
          <ol className="garmin-guide__steps">
            <li>{t(`${prefix}.step1`)}</li>
            <li>{t(`${prefix}.step2`)}</li>
            <li>
              <Trans
                i18nKey={`${prefix}.step3`}
                components={{ strong: <strong className="garmin-guide__highlight" /> }}
              />
            </li>
          </ol>
          <p className="garmin-guide__note">{t(`${prefix}.withoutHistorical`)}</p>
        </>
      ) : (
        <>
          <p className="garmin-guide__note">{t(`${prefix}.body`)}</p>
          <ol className="garmin-guide__steps">
            <li>
              <Trans
                i18nKey={`${prefix}.stepHistorical`}
                components={{ strong: <strong className="garmin-guide__highlight" /> }}
              />
            </li>
            <li>{t(`${prefix}.stepWait`)}</li>
            <li>{t(`${prefix}.stepReconnect`)}</li>
          </ol>
        </>
      )}
    </div>
  );
}

/** Centered dialog for Electron desktop shell. */
function GarminGuideDialog({
  open,
  mode,
  onClose,
  onContinue,
  showLater,
}: GarminConnectSheetProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const titleKey =
    mode === 'connect'
      ? 'athleteSettings.connections.garminConnectSheet.title'
      : 'athleteSettings.connections.garminHelp.title';
  const subtitleKey =
    mode === 'connect' ? 'athleteSettings.connections.garminConnectSheet.subtitle' : undefined;

  return createPortal(
    <div
      className="garmin-guide-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="garmin-guide-dialog" role="dialog" aria-modal="true" aria-labelledby="garmin-guide-title">
        <h2 id="garmin-guide-title" className="garmin-guide-dialog__title">
          {t(titleKey)}
        </h2>
        {subtitleKey && <p className="garmin-guide-dialog__subtitle">{t(subtitleKey)}</p>}
        <GuideSteps mode={mode} />
        <div className="garmin-guide-dialog__actions">
          {mode === 'connect' ? (
            <>
              {showLater && (
                <NmButton variant="ghost" size="sm" onClick={onClose}>
                  {t('athleteSettings.connections.garminConnectSheet.later')}
                </NmButton>
              )}
              <NmButton variant="primary" size="sm" onClick={onContinue}>
                {t('athleteSettings.connections.garminConnectSheet.continue')}
              </NmButton>
            </>
          ) : (
            <NmButton variant="primary" size="sm" onClick={onClose}>
              {t('athleteSettings.connections.garminHelp.close')}
            </NmButton>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Bottom sheet for PWA / browser web. */
function GarminGuideMobileSheet({
  open,
  mode,
  onClose,
  onContinue,
  showLater,
}: GarminConnectSheetProps) {
  const { t } = useTranslation();

  const titleKey =
    mode === 'connect'
      ? 'athleteSettings.connections.garminConnectSheet.title'
      : 'athleteSettings.connections.garminHelp.title';
  const subtitleKey =
    mode === 'connect' ? 'athleteSettings.connections.garminConnectSheet.subtitle' : undefined;

  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      title={t(titleKey)}
      subtitle={subtitleKey ? t(subtitleKey) : undefined}
      sheetClassName="garmin-guide-sheet"
      zIndex={10050}
      footer={
        mode === 'connect' ? (
          <div className="garmin-guide__footer">
            {showLater && (
              <NmButton variant="ghost" size="sm" className="garmin-guide__footer-secondary" onClick={onClose}>
                {t('athleteSettings.connections.garminConnectSheet.later')}
              </NmButton>
            )}
            <NmButton variant="primary" size="sm" className="garmin-guide__footer-primary" onClick={onContinue}>
              {t('athleteSettings.connections.garminConnectSheet.continue')}
            </NmButton>
          </div>
        ) : (
          <NmButton variant="primary" size="sm" style={{ width: '100%' }} onClick={onClose}>
            {t('athleteSettings.connections.garminHelp.close')}
          </NmButton>
        )
      }
    >
      <GuideSteps mode={mode} />
    </MobileSheet>
  );
}

/** Pre-OAuth guide or post-connect troubleshooting for Garmin Historical Data. */
export function GarminConnectSheet(props: GarminConnectSheetProps) {
  if (isElectronShell()) {
    return <GarminGuideDialog {...props} />;
  }
  return <GarminGuideMobileSheet {...props} />;
}
